import { FC, ReactNode } from 'react';

import { MetricsCardSkeleton } from './MetricsCardSkeleton';
import { cva } from 'class-variance-authority';

import { METRICS_CARD_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind';

import { InfoIcon } from '../../../assets/icons';
import { Tooltip } from '../../atoms';
import { Card, CardContent } from '../../atoms/Card/Card';
import { ChangeIndicator } from '../../atoms/ChangeIndicator/ChangeIndicator';

export type MetricsCardProps = {
  title: string;
  value: string | number;
  size?: '1/3' | '2/3' | '3/3';
  changeValue: number;
  changeValueUnit?: 'percent' | 'points';
  isActive?: boolean;
  isLoading?: boolean;
  tooltipTitle?: ReactNode;
  className?: string;
  onClick?: () => void;
};

const titleVariants = cva('', {
  variants: {
    size: {
      '1/3': 'text-body-14-medium !text-custom-warm-grey-500',
      '2/3': 'text-body-14-semibold !text-custom-warm-grey-700',
      '3/3': 'text-body-14-semibold !text-custom-warm-grey-700',
    },
  },
  defaultVariants: {
    size: '2/3',
  },
});

const valueVariants = cva('!text-black', {
  variants: {
    size: {
      '1/3': 'text-body-16-medium',
      '2/3': 'text-body-28-medium',
      '3/3': 'text-body-28-medium',
    },
  },
  defaultVariants: {
    size: '2/3',
  },
});

const cardContentVariants = cva('flex flex-col', {
  variants: {
    size: {
      '1/3': 'gap-2',
      '2/3': 'gap-4',
      '3/3': 'gap-[84px]',
    },
  },
  defaultVariants: {
    size: '2/3',
  },
});

export const MetricsCard: FC<MetricsCardProps> = ({
  title,
  value,
  size = '2/3',
  changeValue,
  changeValueUnit = 'percent',
  isActive,
  isLoading,
  tooltipTitle,
  className,
  onClick,
}) => {
  const isActiveState = isActive && !isLoading;

  if (isLoading) {
    return <MetricsCardSkeleton size={size} />;
  }

  return (
    <Card
      data-testid={METRICS_CARD_TEST_IDS.SKELETON}
      className={cn(
        'cursor-pointer border select-none px-4 py-3',
        isActiveState
          ? 'bg-custom-cyan-25 border-custom-cyan-400'
          : 'bg-custom-warm-grey-25 border-custom-warm-grey-200',
        className
      )}
      onClick={onClick}
    >
      <CardContent className={cn(cardContentVariants({ size }))}>
        <div className="flex items-center justify-between gap-2 min-h-5">
          <p className={cn(titleVariants({ size }))}>{title}</p>
          {isActiveState && (
            <Tooltip title={tooltipTitle}>
              <InfoIcon width={20} height={20} className="text-custom-warm-grey-400" />
            </Tooltip>
          )}
        </div>

        <div className="min-h-[22px] flex items-center gap-2">
          <span className={cn(valueVariants({ size }))}>{value}</span>
          <ChangeIndicator changeValue={changeValue} valueUnit={changeValueUnit} />
        </div>
      </CardContent>
    </Card>
  );
};
